<template>
  <section class="form-list">
    <el-card shadow="never" class="invite-card">
      <div slot="header">生成入职邀请链接</div>
      <el-form ref="inviteForm" :model="form" :rules="rules" label-width="110px" class="invite-form">
        <el-form-item v-if="isAdministrator" label="门店" prop="store_id">
          <v-select-store class="w100" v-model="form.store_id" :has-private="true"/>
        </el-form-item>
        <el-form-item label="职位" prop="administrator_role_id">
          <v-select-administrator-role class="w100" v-model="form.administrator_role_id"/>
        </el-form-item>
        <el-form-item label="有效期">
          <span class="invite-expire-tip">生成后 1 天内有效（当天使用，过期可重新生成）</span>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="isCreating" @click="createInvite">生成链接</el-button>
        </el-form-item>
      </el-form>

      <div v-if="inviteUrl" class="invite-result">
        <el-input readonly :value="inviteUrl">
          <template slot="prepend">链接</template>
        </el-input>
        <div class="qr-wrap">
          <img v-if="qrImage" :src="qrImage" alt="入职二维码"/>
          <a :href="inviteUrl" target="_blank">打开邀请页</a>
        </div>
      </div>
    </el-card>

    <v-table
      class="v-table-header-line-full"
      :count="count"
      :list="list"
      :is-loading="isLoading"
      :push-list="RouteConfig.administratorInviteList.push"
      :query-list="queryList"
      :is-not-top-list="true"
      is-hide-filter
    >
      <el-table-column label="ID" prop="id" width="80"/>
      <el-table-column label="门店" width="180">
        <template v-slot="{ row }">{{ row.store_name | formatStoreName }}</template>
      </el-table-column>
      <el-table-column label="职位" prop="role_name" width="140"/>
      <el-table-column label="邀请人" prop="admin_nickname" width="140"/>
      <el-table-column label="过期时间" width="160">
        <template v-slot="{ row }">{{ row.expire_time | timeToYmdHi }}</template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template v-slot="{ row }">
          <el-tag :type="inviteStatusTag(row)">{{ inviteStatusText(row) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="240">
        <template v-slot="{ row }">
          <v-list-button name="查看" icon="view" :to="RouteConfig.administratorInviteDetail.to(row.id)"/>
          <v-list-button
            v-if="!row.is_del && !isExpired(row) && !isPassedApply(row)"
            name="撤销"
            icon="restore"
            message="确定要撤销该入职邀请吗？撤销后，对方将无法再提交入职申请；已提交的申请不受影响。"
            :is-loading="isRevokingIds.includes(row.id)"
            :is-last="true"
            @click="revoke(row)"
          />
        </template>
      </el-table-column>

      <template slot="mobile-list">
        <section class="list">
          <template v-if="list.length">
            <div
              v-for="(v, k) in list"
              :key="`invite-list-${k}-${v.id}`"
              class="list-item primary-background-light"
            >
              <div class="list-item-detail">
                <span>门店</span>
                <span>{{ v.store_name | formatStoreName }}</span>
              </div>
              <div class="list-item-detail">
                <span>职位</span>
                <span>{{ v.role_name }}</span>
              </div>
              <div class="list-item-detail">
                <span>邀请人</span>
                <span>{{ v.admin_nickname }}</span>
              </div>
              <div class="list-item-detail">
                <span>过期时间</span>
                <span>{{ v.expire_time | timeToYmdHi }}</span>
              </div>
              <div class="list-item-detail">
                <span>状态</span>
                <el-tag :type="inviteStatusTag(v)">{{ inviteStatusText(v) }}</el-tag>
              </div>
              <div class="list-item-button">
                <v-list-button name="查看" icon="view" :to="RouteConfig.administratorInviteDetail.to(v.id)"/>
                <v-list-button
                  v-if="!v.is_del && !isExpired(v) && !isPassedApply(v)"
                  name="撤销"
                  icon="restore"
                  message="确定要撤销该入职邀请吗？撤销后，对方将无法再提交入职申请；已提交的申请不受影响。"
                  :is-loading="isRevokingIds.includes(v.id)"
                  @click="revoke(v)"
                />
              </div>
            </div>
          </template>
          <v-empty :is-loading="isLoading" :length="list.length"/>
        </section>
      </template>
    </v-table>
  </section>
</template>

<script>
import QRCode from 'qrcode';
import { mapGetters } from 'vuex';
import RouteConfig from '@/router/config';
import { inviteIndex, inviteStore, inviteRevoke } from '@/api/personnel';
import { getLegalQueryList } from '@/utils/formats';
import { removeArrayItem } from '@/utils';
// 邀请状态判定与详情页共用同一份逻辑
import {
  inviteStatusText,
  inviteStatusTag,
  isInviteExpired,
  isInvitePassed,
} from '@/enums/invites';

export default {
  name: RouteConfig.administratorInviteList.name,
  data: () => ({
    RouteConfig,
    count: 0,
    isCreating: false,
    isRevokingIds: [],
    isLoading: false,
    list: [],
    inviteUrl: '',
    qrImage: '',
    form: {
      store_id: 0,
      administrator_role_id: 0,
    },
    queryList: {
      page: 1,
      size: 10,
    },
    rules: {
      store_id: [{ required: true, message: '请选择门店', trigger: 'change' }],
      administrator_role_id: [{ required: true, message: '请选择职位', trigger: 'change' }],
    },
  }),
  computed: {
    ...mapGetters(['isAdministrator', 'defaultStoreId']),
  },
  watch: {
    '$route'(to) {
      this.load(to);
    },
  },
  beforeMount() {
    this.form.store_id = this.defaultStoreId;
    this.load();
  },
  methods: {
    refresh() {
      RouteConfig.administratorInviteList.push(this.queryList);
    },
    async load(route = this.$route, next) {
      const { queryList } = this;
      getLegalQueryList(queryList, route.query);

      this.isLoading = true;
      const { isError, data } = await inviteIndex(queryList);
      this.isLoading = false;
      if (isError) return;

      this.count = data.count;
      this.list = data.list || [];
      if (next) next();
    },
    async createInvite() {
      if (this.isCreating) return;

      // 前端先做完整校验，避免无效请求打到后端
      if (this.isAdministrator && !this.form.store_id) {
        this.$message.warning('请选择门店');
        return;
      }
      if (!this.form.administrator_role_id) {
        this.$message.warning('请选择职位');
        return;
      }

      this.$refs.inviteForm.validate(async (isSuccess) => {
        if (!isSuccess) return;
        this.isCreating = true;
        const { isError, data } = await inviteStore(this.form);
        this.isCreating = false;
        if (isError) return;
        this.inviteUrl = data.url;
        this.qrImage = await QRCode.toDataURL(data.url, { width: 260, margin: 1 });
        this.$message.success('邀请链接已生成');
        this.load();
      });
    },
    // 状态判定统一走 @/enums/invites，与邀请详情页共用；以下仅为模板保留同名代理
    isExpired: isInviteExpired,
    isPassedApply: isInvitePassed,
    inviteStatusText,
    inviteStatusTag,
    async revoke(row) {
      if (this.isRevokingIds.includes(row.id)) return;

      this.isRevokingIds.push(row.id);
      const { isError } = await inviteRevoke(row.id);
      removeArrayItem(this.isRevokingIds, row.id);
      if (isError) return;
      this.$message.success('已撤销');
      this.load();
    },
  },
};
</script>

<style scoped>
.invite-card { margin-bottom: 20px; }
.invite-form { max-width: 420px; }
.invite-expire-tip { color: #909399; font-size: 13px; line-height: 1.6; }
.invite-result { margin-top: 16px; padding: 16px; background: #f5f7fa; border-radius: 4px; }
.qr-wrap { margin-top: 14px; text-align: center; }
.qr-wrap img { width: 260px; height: 260px; }
.qr-wrap a { display: block; margin-top: 8px; }
</style>
